import { graphqlClient } from './graphql-client.js';
import { MapManager } from './map.js';
import { UIManager } from './ui.js';
import { i18n } from './i18n.js';

const DEBUG = import.meta.env.DEV;

class MapApp {
  constructor() {
    this.mapManager = null;
    this.uiManager = new UIManager();
    this.currentSpots = [];
    this.loadingSpots = false;
    this.queryDebounceTimer = null;
  }

  async init() {
    try {
      if (DEBUG) {
        console.log('🚀 Initializing app');
      }

      // Initialize translations
      const translationsLoaded = await i18n.init();
      if (!translationsLoaded) {
        console.warn('⚠️ Translations not loaded, proceeding with defaults');
      }

      // Apply translations to HTML
      this.applyTranslations();

      // Initialize map
      this.mapManager = new MapManager('map');
      this.mapManager.init();

      // Setup marker click handler for navigation
      this.setupMarkerClickHandler();

      // Handle map changes (pan/zoom)
      this.mapManager.onBoundsChange(() => {
        this.onMapBoundsChange();
      });

      // Initial load
      await this.loadSpots();

      if (DEBUG) {
        console.log('✓ App initialized');
      }
    } catch (error) {
      console.error('✗ Failed to initialize app:', error);
      this.uiManager.showError(i18n.t('error.failed'));
    }
  }

  applyTranslations() {
    // Apply data-i18n attributes
    document.querySelectorAll('[data-i18n]').forEach(el => {
      const key = el.getAttribute('data-i18n');
      const translated = i18n.t(key);
      if (el.tagName === 'INPUT' || el.tagName === 'BUTTON') {
        el.placeholder = translated;
      } else {
        el.textContent = translated;
      }
    });

    // Apply data-i18n-template attributes (for templates with placeholders)
    document.querySelectorAll('[data-i18n-template]').forEach(el => {
      el.dataset.i18nTemplate = el.getAttribute('data-i18n-template');
    });
  }

  updateTranslatableElements(count) {
    const countElement = document.getElementById('spot-count');
    if (countElement && countElement.dataset.i18nTemplate) {
      const key = countElement.dataset.i18nTemplate;
      countElement.textContent = i18n.t(key, { count });
    }
  }

  async onMapBoundsChange() {
    if (this.loadingSpots) {
      return;
    }

    await this.loadSpots();
  }

  async loadSpots() {
    if (this.loadingSpots) {
      return;
    }

    this.loadingSpots = true;
    this.uiManager.showLoading();

    try {
      const bounds = this.mapManager.getCurrentBoundsForQuery();

      if (DEBUG) {
        console.log('📡 Loading spots for bounds:', bounds);
      }

      const spots = await graphqlClient.getSpotsInBounds(bounds);

      if (DEBUG) {
        console.log(`✓ Loaded ${spots.length} spots`);
      }

      this.currentSpots = spots;
      this.mapManager.addSpots(spots);

      this.updateTranslatableElements(spots.length);

      this.uiManager.hideLoading();
    } catch (error) {
      console.error('✗ Failed to load spots:', error);
      this.uiManager.showError(error.message);
    } finally {
      this.loadingSpots = false;
    }
  }

  setupMarkerClickHandler() {
    // Override the map manager's marker creation to navigate on click
    const originalAddSpots = this.mapManager.addSpots.bind(this.mapManager);
    
    this.mapManager.addSpots = (spots) => {
      spots.forEach(spot => {
        if (this.mapManager.markers.has(spot.id)) {
          return;
        }

        const marker = this.mapManager.createMarker(spot);
        
        // Override popup button click to navigate
        marker.on('popupopen', () => {
          const button = document.querySelector('.popup-button');
          if (button) {
            button.onclick = (e) => {
              e.preventDefault();
              window.location.href = `/spot/${spot.id}`;
            };
          }
        });

        marker.addTo(this.mapManager.map);
        this.mapManager.markers.set(spot.id, marker);
      });
    };
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const app = new MapApp();
    app.init();
    window.mapApp = app;
  });
} else {
  const app = new MapApp();
  app.init();
  window.mapApp = app;
}
